import { AlertCircle, AlertTriangle, CheckCircle2, Copy, Loader2, Mail, RefreshCw, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import toastService from '@/services/toast/service';
import type { AiEmailInfo, EmailPreview, PreviewAccount } from '@/services/aiPreview';
import { TRIAL_RISK_LABELS, capitalize, wordCount } from './shared';

const PICKED = '__picked__';

interface EmailTabProps {
  account: PreviewAccount;
  emails: AiEmailInfo[];
  preview: EmailPreview | null;
  loading: boolean;
  error: string | null;
  choice: string | null;
  canRun: boolean;
  onChoose: (emailKey: string | null) => void;
  onWrite: () => void;
}

export function EmailTab({ account, emails, preview, loading, error, choice, canRun, onChoose, onWrite }: EmailTabProps) {
  const nameOf = (key: string | null) => emails.find((e) => e.key === key)?.name ?? key ?? '';

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start">
      <div className="flex flex-col gap-5">
        <Card className="gap-3 px-5 py-4">
          <span className="text-xs font-medium text-muted-foreground">Picked email</span>
          {preview ? (
            preview.picked ? (
              <div className="flex flex-col gap-1">
                <span className="font-semibold">{nameOf(preview.picked)}</span>
                <span className="font-mono text-xs text-muted-foreground">{preview.picked}</span>
              </div>
            ) : (
              <span className="text-sm">No email fits this account right now. The job only writes to trials.</span>
            )
          ) : (
            <span className="text-sm text-muted-foreground">Write the email to see which one the job picks.</span>
          )}
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{account.billing === 'trialing' ? 'On a trial' : capitalize(account.billing)}</Badge>
            {TRIAL_RISK_LABELS[account.trialRisk] && <Badge tone="neutral">Trial risk: {TRIAL_RISK_LABELS[account.trialRisk].label.toLowerCase()}</Badge>}
            {preview && (
              <Badge tone="neutral">
                {preview.facts.hasForm === false ? 'No form yet' : preview.facts.hasEmbed ? 'A form is embedded on a page' : 'No form on a page yet'}
              </Badge>
            )}
          </div>

          <div className="flex flex-col gap-1.5 pt-2">
            <span className="text-xs font-medium text-muted-foreground">Try another email</span>
            <Select value={choice ?? PICKED} onValueChange={(v) => onChoose(v === PICKED ? null : v)}>
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PICKED}>The one the job picks</SelectItem>
                {emails.map((e) => (
                  <SelectItem key={e.key} value={e.key}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {preview && (
          <Card className="gap-0 py-0">
            <div className="px-5 py-4 border-b">
              <h2 className="text-base font-semibold">Would the job send it now?</h2>
            </div>
            <ul className="px-5 py-3 flex flex-col">
              {preview.guards.map((g) => (
                <li key={g.key} className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0 text-[13px]">
                  <span className="flex items-center gap-2">
                    {g.ok ? (
                      <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    ) : (
                      <XCircle className="size-4 text-red-600 dark:text-red-400 shrink-0" />
                    )}
                    {g.label}
                  </span>
                  <span className={cn('text-right', g.ok ? 'text-muted-foreground' : 'text-foreground')}>{g.detail}</span>
                </li>
              ))}
            </ul>
            <div className="px-5 pb-4 flex flex-col gap-2">
              <Verdict preview={preview} nameOf={nameOf} />
              {preview.fieldsFrom === 'database' && (
                <p className="text-xs text-muted-foreground">
                  No GHL contact to read, so this uses our database. The job reads the GHL contact, and skips accounts without one.
                </p>
              )}
              {preview.outOfDate.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  GHL is behind our database ({preview.outOfDate.map((f) => `${f.name}: ${f.ghl || 'empty'} in GHL, ${f.now || 'empty'} now`).join('; ')}).
                  The job picks from GHL; the next sync updates it.
                </p>
              )}
            </div>
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {!preview && !loading && (
          <Card className="items-center text-center gap-3 px-5 py-10">
            <Mail className="size-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-sm">
              Writes the email with the AI, from the scans on the first tab and the saved prompts. It takes up to a minute.
            </p>
            {error && (
              <p className="flex items-center gap-2 text-sm">
                <AlertCircle className="size-4 text-destructive" />
                {error}
              </p>
            )}
            <Button disabled={!canRun} onClick={onWrite}>
              <Mail />
              Write the email
            </Button>
          </Card>
        )}

        {loading && (
          <Card role="status" className="flex-row items-center justify-center gap-2 px-5 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Writing the email. The AI checks it and tries again if it breaks a rule, so this can take a minute.
          </Card>
        )}

        {preview && !loading && !preview.email && (
          <Card className="px-5 py-6 text-sm text-muted-foreground">
            No email to write: none fits this account right now. Pick one under "Try another email" to see it anyway.
          </Card>
        )}

        {preview?.email && !loading && (
          <>
            <EmailCard
              from="Jovan · RoasForm"
              to={preview.to}
              subject={preview.email.subject}
              body={preview.email.body}
              bookingUrl={preview.bookingUrl}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                {preview.email.problems.length === 0 ? (
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="size-4" />
                    All checks passed
                  </span>
                ) : (
                  <span className="flex items-start gap-1.5 text-[13px] font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                    The job would hold this one: {preview.email.problems.join('; ')}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {preview.email.attempts === 1 ? 'First try' : `Try ${preview.email.attempts} of 3`} · {preview.email.model} ·{' '}
                  {wordCount(preview.email.body)} words
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigator.clipboard
                      .writeText(`${preview.email!.subject}\n\n${preview.email!.body}`)
                      .then(() => toastService.success('Copied'))
                  }
                >
                  <Copy />
                  Copy text
                </Button>
                <Button variant="outline" size="sm" disabled={!canRun} onClick={onWrite}>
                  <RefreshCw />
                  Write again
                </Button>
              </div>
            </div>
          </>
        )}
        <p className="text-xs text-muted-foreground">Preview emails aren't saved anywhere and don't count toward the cooldown.</p>
      </div>
    </div>
  );
}

function Verdict({ preview, nameOf }: { preview: EmailPreview; nameOf: (key: string | null) => string }) {
  if (preview.wouldSend) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[13px] text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-200">
        {
          {
            draft: 'The job would write this email as a draft on its next run.',
            ghl_api: 'The job would send this email on its next run.',
            ghl_workflow: 'The job would hand this email to the GHL workflow on its next run.',
          }[preview.guards.find((g) => g.key === 'delivery')?.detail ?? ''] ?? 'The job would write this email on its next run.'
        }
      </div>
    );
  }
  const failed = preview.guards.find((g) => !g.ok);
  let reason: string;
  if (preview.picked && preview.emailKey !== preview.picked) reason = `The job would write "${nameOf(preview.picked)}" instead, not this one.`;
  else if (!preview.picked) reason = 'No email fits this account right now, so the job would not write to it.';
  else if (failed?.key === 'pilot') reason = 'Not in the AI pilot, so the job would not email this account. Add it to the pilot to start.';
  else reason = `The job would not send it now: ${failed?.label.toLowerCase()} (${failed?.detail}).`;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
      {reason}
    </div>
  );
}

/** Mirrors the email template (ai-emails/render.ts): the body, the booking button, the signature and the opt-out line. */
export function EmailCard({
  from,
  to,
  subject,
  body,
  bookingUrl,
}: {
  from: string;
  to: string | null;
  subject: string;
  body: string;
  bookingUrl: string | null;
}) {
  return (
    <Card className="gap-0 py-0 overflow-hidden">
      <dl className="grid grid-cols-[70px_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-5 py-3.5 border-b text-[13px] bg-muted/30">
        <dt className="text-muted-foreground">From</dt>
        <dd>{from}</dd>
        <dt className="text-muted-foreground">To</dt>
        <dd className="break-all">{to ?? 'Unknown'}</dd>
        <dt className="text-muted-foreground">Subject</dt>
        <dd className="font-semibold">{subject}</dd>
      </dl>
      <div className="px-5 py-5 flex flex-col gap-3.5 text-sm leading-relaxed">
        {body
          .trim()
          .split(/\n\s*\n/)
          .map((p, i) => (
            <p key={i} className="whitespace-pre-line">
              {p.trim()}
            </p>
          ))}
        <div>
          {bookingUrl ? (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Book 15 minutes with me
            </a>
          ) : (
            <span
              title="AI_EMAIL_BOOKING_URL isn't set"
              className="inline-flex rounded-md bg-primary/60 px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Book 15 minutes with me
            </span>
          )}
        </div>
        <p>
          Jovan
          <br />
          Co-founder, RoasForm
        </p>
        <p className="text-xs text-muted-foreground">Not useful right now? Reply "stop" and I won't send more.</p>
      </div>
    </Card>
  );
}
